/**
 * aeterna/neural/affect-mlp.ts — Pre-trained Affect Classification MLP
 *
 * Replaces the CharCNN's random-seed convolution pipeline with a
 * distilled, quantized MLP trained on GoEmotions (58K labeled utterances,
 * 28 emotion classes → 8 affect dimensions via projection).
 *
 * Architecture:
 *   Input: 2048-dim character trigram hash features (zero external deps)
 *   Layer 1: 2048 → 128, ReLU, int8 quantized
 *   Layer 2: 128 → 64, ReLU, int8 quantized
 *   Layer 3: 64 → 8, Sigmoid, int8 quantized
 *
 * Total weight size: ~271KB (int8) + biases/scales (~1KB)
 *   Layer 1: 2048 × 128 = 262,144 bytes
 *   Layer 2: 128 × 64  = 8,192 bytes
 *   Layer 3: 64 × 8    = 512 bytes
 *
 * The weights are loaded from a bundled binary asset (affect-mlp.bin).
 * Until loaded, the module is inactive and the caller should fall back
 * to the CharCNN + lexicon pipeline.
 *
 * Why character trigram hashing instead of word tokenization?
 *   - Zero vocab dependency; works on any UTF-8 input including typos
 *   - Feature extraction is O(n) with no tokenizer overhead
 *   - Hash collisions are a feature: similar trigrams share buckets
 *   - FNV-1a hashing is cache-friendly and branch-free
 */

import { zeros, vsigmoid } from "./math.js";
import type { CharAffect } from "./charcnn.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Number of hash buckets for trigram features */
const HASH_BUCKETS = 2048;

/** Layer dimensions */
const L1_OUT = 128;
const L2_OUT = 64;
const OUTPUT_DIM = 8;

// ─── Feature Extraction ──────────────────────────────────────────────────────

/**
 * FNV-1a hash for a character trigram → bucket index.
 * Fast, well-distributed, no external dependency.
 */
function fnv1a(a: number, b: number, c: number): number {
  let h = 0x811c9dc5;
  h ^= a; h = Math.imul(h, 0x01000193);
  h ^= b; h = Math.imul(h, 0x01000193);
  h ^= c; h = Math.imul(h, 0x01000193);
  return (h >>> 0) % HASH_BUCKETS;
}

/**
 * Extract character trigram hash features from text.
 * Returns a sparse 2048-dim vector (bag of trigrams).
 * Normalized by trigram count for length invariance.
 */
function extractTrigramFeatures(text: string): Float32Array {
  const features = new Float32Array(HASH_BUCKETS);
  const lower = text.toLowerCase();
  const len = lower.length;

  if (len < 3) {
    // For very short text, use unigrams and bigrams too
    for (let i = 0; i < len; i++) {
      const c = lower.charCodeAt(i) & 0xFF;
      features[fnv1a(c, 0, 0)] += 1;
    }
    if (len >= 2) {
      const a = lower.charCodeAt(0) & 0xFF;
      const b = lower.charCodeAt(1) & 0xFF;
      features[fnv1a(a, b, 0)] += 1;
    }
  } else {
    const trigramCount = len - 2;
    for (let i = 0; i < trigramCount; i++) {
      const a = lower.charCodeAt(i) & 0xFF;
      const b = lower.charCodeAt(i + 1) & 0xFF;
      const c = lower.charCodeAt(i + 2) & 0xFF;
      features[fnv1a(a, b, c)] += 1;
    }
    // Normalize by trigram count
    if (trigramCount > 0) {
      const invCount = 1 / trigramCount;
      for (let i = 0; i < HASH_BUCKETS; i++) {
        features[i] *= invCount;
      }
    }
  }

  return features;
}

// ─── Int8 MLP Layer ──────────────────────────────────────────────────────────

interface Int8Layer {
  /** Int8 weight matrix (rows × cols, stored flat, row-major) */
  weights: Int8Array;
  /** Float32 bias vector */
  bias: Float32Array;
  /** Dequantization scale: float_weight = int8_weight × scale */
  scale: number;
  /** Input dimension */
  inputDim: number;
  /** Output dimension */
  outputDim: number;
}

/**
 * Forward pass through an int8 layer.
 * Dequantizes on the fly during matrix-vector multiplication.
 * ReLU activation applied inline (caller can override for output layer).
 */
function int8Forward(layer: Int8Layer, input: Float32Array, relu: boolean): Float32Array {
  const out = new Float32Array(layer.outputDim);
  const { weights, bias, scale, inputDim, outputDim } = layer;

  for (let o = 0; o < outputDim; o++) {
    let sum = bias[o];
    const rowOff = o * inputDim;
    for (let i = 0; i < inputDim; i++) {
      sum += (weights[rowOff + i] * scale) * input[i];
    }
    out[o] = relu ? Math.max(0, sum) : sum;
  }

  return out;
}

// ─── Affect MLP ──────────────────────────────────────────────────────────────

export class AffectMLP {
  private layer1: Int8Layer | null = null;
  private layer2: Int8Layer | null = null;
  private layer3: Int8Layer | null = null;
  private loaded = false;

  /** Is the model ready for inference? */
  isLoaded(): boolean { return this.loaded; }

  /**
   * Load weights from a packed binary buffer.
   *
   * Binary format:
   *   [4 bytes] magic: 0x41464D4C ("AFML")
   *   [4 bytes] version: 1
   *   [4 bytes] float32 scale1
   *   [4 bytes] float32 scale2
   *   [4 bytes] float32 scale3
   *   [L1_OUT × 4 bytes] float32 bias1
   *   [L2_OUT × 4 bytes] float32 bias2
   *   [OUTPUT_DIM × 4 bytes] float32 bias3
   *   [HASH_BUCKETS × L1_OUT bytes] int8 weights1
   *   [L1_OUT × L2_OUT bytes] int8 weights2
   *   [L2_OUT × OUTPUT_DIM bytes] int8 weights3
   */
  loadFromBuffer(buffer: ArrayBuffer): boolean {
    const view = new DataView(buffer);
    let offset = 0;

    // Magic check
    const magic = view.getUint32(offset, true); offset += 4;
    if (magic !== 0x41464D4C) {
      console.warn("[AffectMLP] Invalid magic number in weight file");
      return false;
    }

    // Version check
    const version = view.getUint32(offset, true); offset += 4;
    if (version !== 1) {
      console.warn(`[AffectMLP] Unsupported version: ${version}`);
      return false;
    }

    // Scales
    const scale1 = view.getFloat32(offset, true); offset += 4;
    const scale2 = view.getFloat32(offset, true); offset += 4;
    const scale3 = view.getFloat32(offset, true); offset += 4;

    // Biases
    const bias1 = new Float32Array(L1_OUT);
    for (let i = 0; i < L1_OUT; i++) {
      bias1[i] = view.getFloat32(offset, true); offset += 4;
    }
    const bias2 = new Float32Array(L2_OUT);
    for (let i = 0; i < L2_OUT; i++) {
      bias2[i] = view.getFloat32(offset, true); offset += 4;
    }
    const bias3 = new Float32Array(OUTPUT_DIM);
    for (let i = 0; i < OUTPUT_DIM; i++) {
      bias3[i] = view.getFloat32(offset, true); offset += 4;
    }

    // Int8 weights
    const w1Size = HASH_BUCKETS * L1_OUT;
    const weights1 = new Int8Array(buffer, offset, w1Size); offset += w1Size;
    const w2Size = L1_OUT * L2_OUT;
    const weights2 = new Int8Array(buffer, offset, w2Size); offset += w2Size;
    const w3Size = L2_OUT * OUTPUT_DIM;
    const weights3 = new Int8Array(buffer, offset, w3Size); offset += w3Size;

    this.layer1 = { weights: weights1, bias: bias1, scale: scale1, inputDim: HASH_BUCKETS, outputDim: L1_OUT };
    this.layer2 = { weights: weights2, bias: bias2, scale: scale2, inputDim: L1_OUT, outputDim: L2_OUT };
    this.layer3 = { weights: weights3, bias: bias3, scale: scale3, inputDim: L2_OUT, outputDim: OUTPUT_DIM };
    this.loaded = true;

    return true;
  }

  /**
   * Classify text into 8-dim affect vector.
   * Returns null if weights haven't been loaded.
   */
  classify(text: string): CharAffect | null {
    if (!this.loaded || !this.layer1 || !this.layer2 || !this.layer3) {
      return null;
    }

    // Feature extraction
    const features = extractTrigramFeatures(text);

    // Forward pass: trigrams → 128 (ReLU) → 64 (ReLU) → 8 (sigmoid)
    const h1 = int8Forward(this.layer1, features, true);
    const h2 = int8Forward(this.layer2, h1, true);
    const logits = int8Forward(this.layer3, h2, false);
    const output = vsigmoid(logits);

    // Compute intensity
    let intensitySum = 0;
    for (let i = 0; i < OUTPUT_DIM; i++) {
      const dev = output[i] - 0.5;
      intensitySum += dev * dev;
    }
    const intensity = Math.sqrt(intensitySum / OUTPUT_DIM) * 2;

    return {
      warmth:       output[0],
      tension:      output[1],
      curiosity:    output[2],
      melancholy:   output[3],
      playfulness:  output[4],
      depth:        output[5],
      urgency:      output[6],
      openness:     output[7],
      intensity,
    };
  }

  /**
   * Classify and return raw Float32Array (for Hopfield, ESN, etc.)
   * Returns null if weights haven't been loaded.
   */
  classifyRaw(text: string): Float32Array | null {
    if (!this.loaded || !this.layer1 || !this.layer2 || !this.layer3) {
      return null;
    }

    const features = extractTrigramFeatures(text);
    const h1 = int8Forward(this.layer1, features, true);
    const h2 = int8Forward(this.layer2, h1, true);
    const logits = int8Forward(this.layer3, h2, false);
    return vsigmoid(logits);
  }

  /** Expected binary file size for validation */
  static expectedFileSize(): number {
    const headerSize = 4 + 4; // magic + version
    const scaleSize = 3 * 4;  // 3 float32 scales
    const biasSize = (L1_OUT + L2_OUT + OUTPUT_DIM) * 4; // float32 biases
    const weightSize = HASH_BUCKETS * L1_OUT + L1_OUT * L2_OUT + L2_OUT * OUTPUT_DIM; // int8 weights
    return headerSize + scaleSize + biasSize + weightSize;
    // = 8 + 12 + 800 + 270848 = 271668 bytes ≈ 265KB
  }
}

/** Singleton instance — load once, share across cortex */
export const affectMLP = new AffectMLP();
